import { useState } from 'react';
import {
  HUSTLE_DETAILS_MAX,
  HustlePostRequest,
  formatPence,
  hustleMax,
  transferFee,
  type HustleContent,
  type HustleKind,
  type HustleReply,
} from '@london-life/contracts';
import { typedPence } from '../lib/format';

// what a job is, then one button: the same form posts on the Board and offers a job to one player. The fee is
// checked against the kind's most; the total says what is held when someone is hired (or at once, for an offer).
export function JobForm({
  hustle,
  reply,
  kinds,
  firstKind,
  firstFee,
  offerTo,
  busy,
  onSend,
}: {
  hustle: HustleContent;
  reply: HustleReply;
  // the kinds to choose from (an offer: the ones the player listed)
  kinds: HustleKind[];
  firstKind?: HustleKind;
  firstFee?: number;
  // the player an offer goes to (none: a post on the Board)
  offerTo?: string;
  busy: boolean;
  onSend: (body: HustlePostRequest) => void;
}) {
  const shown = hustle.kinds.filter((item) => item.visible && kinds.includes(item.kind));
  const [kind, setKind] = useState<HustleKind>(firstKind ?? shown[0]?.kind ?? 'custom');
  const [details, setDetails] = useState('');
  const [fee, setFee] = useState(firstFee ? String(firstFee / 100) : '');
  const chosen = hustle.kinds.find((item) => item.kind === kind);
  const feePence = typedPence(fee);
  const max = hustleMax(hustle, kind);
  const charge = feePence
    ? transferFee(feePence, { transferFeeBps: reply.charge.bps, transferFeeMinPence: reply.charge.minPence })
    : 0;
  const checked = HustlePostRequest.safeParse({ kind, details, feePence });
  const problem = !fee
    ? null
    : feePence < hustle.feeMinPence || feePence > max
      ? `A fee is ${formatPence(hustle.feeMinPence)} to ${formatPence(max)}`
      : null;
  const wordProblem =
    details.trim() && !checked.success
      ? (checked.error.issues.find((issue) => issue.path[0] === 'details')?.message ?? null)
      : null;
  return (
    <div className="hustle-form">
      <div className="business-chips" role="radiogroup" aria-label="What kind of job">
        {shown.map((item) => (
          <button
            key={item.kind}
            type="button"
            role="radio"
            aria-checked={kind === item.kind}
            className={kind === item.kind ? 'active' : ''}
            onClick={() => setKind(item.kind)}
          >
            {item.emoji} {item.label}
          </button>
        ))}
      </div>
      <label className="railco-field">
        <span>WHAT THE JOB IS</span>
        <textarea
          value={details}
          rows={2}
          maxLength={HUSTLE_DETAILS_MAX}
          placeholder={chosen ? `${chosen.label}: when, where, what you need` : 'When, where, what you need'}
          onChange={(event) => setDetails(event.target.value)}
        />
        <small className={wordProblem ? 'railco-problem' : ''}>
          {wordProblem ?? `${details.trim().length}/${HUSTLE_DETAILS_MAX}`}
        </small>
      </label>
      <label className="railco-field">
        <span>THE FEE (£)</span>
        <input inputMode="decimal" value={fee} placeholder="50" onChange={(event) => setFee(event.target.value)} />
        <small className={problem ? 'railco-problem' : ''}>
          {problem ??
            (feePence
              ? offerTo
                ? `You pay ${formatPence(feePence + charge)} now (the fee + ${formatPence(charge)} charge), held until the job is done. It comes back if they say no, don't answer within ${hustle.offerHours} hours, or you call it off.`
                : `You pay ${formatPence(feePence + charge)} when you hire (the fee + ${formatPence(charge)} charge), held until the job is done.`
              : 'Nothing is paid until you hire someone.')}
        </small>
      </label>
      <button
        type="button"
        className="city-card-go"
        disabled={
          busy || !checked.success || Boolean(problem) || (Boolean(offerTo) && reply.walletPence < feePence + charge)
        }
        onClick={() => checked.success && onSend(checked.data)}
      >
        {offerTo ? `Send ${offerTo} the offer` : 'Post the job'}
      </button>
    </div>
  );
}
